import React, { useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { PageIndices } from '@/common/i-doc/i-doc.registry.ts';
import { AiAnalysisModal } from '@/components/reports/AiAnalysisModal';
import { AnyEvent, EventType } from './types';
import { DateNavigator } from './DateNavigator';
import { SummaryRings } from './SummaryRings';
import { EventTimeline } from './EventTimeline';
import { EventDetailModal } from './EventDetailModal';
import { BottomBar } from './BottomBar';
import { useEventsForDate } from './useEventsForDate';
import { QuickFoodAddModalV2 } from '@/components/dashboard/QuickFoodAddModalV2';
import { QuickWaterAddModal } from '@/components/dashboard/QuickWaterAddModal';
import { QuickWorkoutAddModalV2 } from '@/components/dashboard/QuickWorkoutAddModalV2';
import { QuickMedicationAddModalV2 } from '@/components/dashboard/QuickMedicationAddModalV2';
import { AddFeelingEventModalV2 } from '@/components/calendar/add-event-modals/AddFeelingEventModalV2';
import { AddTaskEventModalV2 } from '@/components/calendar/add-event-modals/AddTaskEventModalV2';
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog';
import { createFeelingEvent, updateFeelingEvent, deleteCalendarEvent, completeEvent, updateCalendarEvent } from '@/services/calendar.service';
import { toLocalIso } from '@/utils/datetime';
import { createTaskEvent, updateTaskEvent, CreateTaskEventDto, UpdateTaskEventDto } from '@/services/task.service';
import { toastService } from '@/services/toast.service';
import { TodayDashboardResponse } from '@/types/dashboard.types';
import { AddFoodEventModal } from '@/components/calendar/add-event-modals/AddFoodEventModal';
import { AddWorkoutEventModal } from '@/components/calendar/add-event-modals/AddWorkoutEventModal';
import { AddMedicationEventModal } from '@/components/calendar/add-event-modals/AddMedicationEventModal';
import { saveFoodEvent } from '@/services/food.service';
import { saveWorkoutIntake } from '@/services/workout.service';
import { saveMedicationIntake } from '@/services/medication.service';
import { CalendarEventDto } from '@/types/calendar';

const toLocalDateStr = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const today = () => toLocalDateStr(new Date());

interface DashboardPageV2Props {
  dashboardData?: TodayDashboardResponse | null;
  dashboardLoading?: boolean;
  selectedDate?: string;
  onDateChange?: (date: string) => void;
  onRefresh?: () => void;
}

export const DashboardPageV2: React.FC<DashboardPageV2Props> = ({
  dashboardData,
  dashboardLoading,
  selectedDate: controlledDate,
  onDateChange: controlledOnDateChange,
  onRefresh,
}) => {
  const [internalDate, setInternalDate] = useState(today);
  const date = controlledDate ?? internalDate;
  const setDate = controlledOnDateChange ?? setInternalDate;
  const [selectedEvent, setSelectedEvent] = useState<AnyEvent | null>(null);
  const [eventToDelete, setEventToDelete] = useState<AnyEvent | null>(null);
  const [editingAiFood, setEditingAiFood] = useState<AnyEvent | null>(null);
  const [editingAiWorkout, setEditingAiWorkout] = useState<AnyEvent | null>(null);
  const [editingAiMedication, setEditingAiMedication] = useState<AnyEvent | null>(null);
  const [editingWater, setEditingWater] = useState<CalendarEventDto | null>(null);
  const [editingFeeling, setEditingFeeling] = useState<CalendarEventDto | null>(null);
  const [editingManualFood, setEditingManualFood] = useState<CalendarEventDto | null>(null);
  const [editingManualWorkout, setEditingManualWorkout] = useState<CalendarEventDto | null>(null);
  const [editingManualMedication, setEditingManualMedication] = useState<CalendarEventDto | null>(null);
  const [editingTask, setEditingTask] = useState<CalendarEventDto | null>(null);
  const [manualFoodModalOpen, setManualFoodModalOpen] = useState(false);
  const [manualWorkoutModalOpen, setManualWorkoutModalOpen] = useState(false);
  const [manualMedicationModalOpen, setManualMedicationModalOpen] = useState(false);
  const [foodModalOpen, setFoodModalOpen]           = useState(false);
  const [workoutModalOpen, setWorkoutModalOpen]     = useState(false);
  const [medicationModalOpen, setMedicationModalOpen] = useState(false);
  const [feelingModalOpen, setFeelingModalOpen]     = useState(false);
  const [taskModalOpen, setTaskModalOpen]           = useState(false);
  const [waterModalOpen, setWaterModalOpen]         = useState(false);
  const [aiAnalysisOpen, setAiAnalysisOpen]         = useState(false);

  const handleQuickAdd = (type: EventType) => {
    if (type === 'food')       setFoodModalOpen(true);
    if (type === 'workout')    setWorkoutModalOpen(true);
    if (type === 'medication') setMedicationModalOpen(true);
    if (type === 'feeling')    setFeelingModalOpen(true);
    if (type === 'task')       setTaskModalOpen(true);
    if (type === 'liquid')     setWaterModalOpen(true);
  };

  const handleSaveFeeling = async (data: { title: string; scheduledLdt?: string; isCompleted: boolean }) => {
    const scheduledLdt = data.scheduledLdt ?? toLocalIso(new Date(`${date}T${new Date().toTimeString().slice(0, 5)}`));
    if (editingFeeling) {
      await updateFeelingEvent(editingFeeling.id, { title: data.title, scheduledLdt, isCompleted: data.isCompleted });
      toastService.success('Думки успішно оновлено');
    } else {
      await createFeelingEvent({ title: data.title, scheduledLdt, isCompleted: true });
      toastService.success('Думки успішно додано');
    }
    setFeelingModalOpen(false);
    setEditingFeeling(null);
    refetch();
  };

  const handleSaveTask = async (data: CreateTaskEventDto | UpdateTaskEventDto) => {
    if (editingTask) {
      await updateTaskEvent(editingTask.id, data as UpdateTaskEventDto);
      toastService.success('Задачу успішно оновлено');
    } else {
      await createTaskEvent(data as CreateTaskEventDto);
      toastService.success('Задачу успішно додано');
    }
    setTaskModalOpen(false);
    setEditingTask(null);
    refetch();
  };

  const handleSaveManualFood = async (data: any) => {
    try {
      await saveFoodEvent(data, { eventId: editingManualFood!.id });
      toastService.success('Прийом їжі успішно оновлено');
      setManualFoodModalOpen(false); setEditingManualFood(null); refetch();
    } catch (e) { toastService.error('Не вдалося зберегти'); }
  };

  const handleSaveManualWorkout = async (data: any) => {
    try {
      await saveWorkoutIntake(data, { eventId: editingManualWorkout!.id });
      toastService.success('Тренування успішно оновлено');
      setManualWorkoutModalOpen(false); setEditingManualWorkout(null); refetch();
    } catch (e) { toastService.error('Не вдалося зберегти'); }
  };

  const handleSaveManualMedication = async (data: any) => {
    try {
      await saveMedicationIntake(data, { eventId: editingManualMedication!.id });
      toastService.success('Прийом препарату успішно оновлено');
      setManualMedicationModalOpen(false); setEditingManualMedication(null); refetch();
    } catch (e) { toastService.error('Не вдалося зберегти'); }
  };

  const handleToggleComplete = async (eventId: number, isCompleted: boolean) => {
    try {
      if (isCompleted) {
        await completeEvent(eventId);
      } else {
        await updateCalendarEvent(eventId, { isCompleted: false });
      }
      toastService.success(`Статус оновлено`);
      refetch();
    } catch (error) {
      console.error('Failed to update event status:', error);
      toastService.error('Не вдалося оновити статус');
    }
  };

  const handleDeleteRequest = (ev: AnyEvent) => {
    setSelectedEvent(null);
    setEventToDelete(ev);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;
    try {
      await deleteCalendarEvent(eventToDelete.id);
      toastService.success('Подію видалено');
      setEventToDelete(null);
      refetch();
    } catch {
      toastService.error('Не вдалося видалити подію');
    }
  };

  const handleEdit = (ev: AnyEvent) => {
    if (ev.type === 'food' && ev.raw?.source === 'ai') {
      setEditingAiFood(ev); setFoodModalOpen(true);
    } else if (ev.type === 'workout' && ev.raw?.source === 'ai') {
      setEditingAiWorkout(ev); setWorkoutModalOpen(true);
    } else if (ev.type === 'medication' && ev.raw?.source === 'ai') {
      setEditingAiMedication(ev); setMedicationModalOpen(true);
    } else if (ev.type === 'food' && ev.raw) {
      setEditingManualFood(ev.raw); setManualFoodModalOpen(true);
    } else if (ev.type === 'workout' && ev.raw) {
      setEditingManualWorkout(ev.raw); setManualWorkoutModalOpen(true);
    } else if (ev.type === 'medication' && ev.raw) {
      setEditingManualMedication(ev.raw); setManualMedicationModalOpen(true);
    } else if (ev.type === 'liquid' && ev.raw) {
      setEditingWater(ev.raw); setWaterModalOpen(true);
    } else if (ev.type === 'feeling' && ev.raw) {
      setEditingFeeling(ev.raw); setFeelingModalOpen(true);
    } else if (ev.type === 'task' && ev.raw) {
      setEditingTask(ev.raw); setTaskModalOpen(true);
    }
  };

  const { events, loading, error, refetch: refetchEvents } = useEventsForDate(date);

  const refetch = () => {
    refetchEvents();
    onRefresh?.();
  };

  return (
    <Box sx={{ px: 1.5, pb: 12 }} data-i-doc={PageIndices.DASHBOARD_V2}>

      <DateNavigator date={date} onDateChange={setDate} />

      <SummaryRings date={date} dashboardData={dashboardData} loading={dashboardLoading} />

      {/* AI-аналіз дня — тільки якщо є хоча б одна подія */}
      {!loading && events.length > 0 && <Paper
        variant="outlined"
        sx={{ mx: 0, my: 1.5, p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}
      >
        <Box>
          <Typography variant="subtitle2" fontWeight={600}>AI-аналіз дня</Typography>
          <Typography variant="caption" color="text.secondary">
            Персоналізований аналіз вашої активності
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => setAiAnalysisOpen(true)}
        >
          Аналізувати
        </Button>
      </Paper>}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {error && !loading && (
        <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 2 }}>
          {error}
        </Typography>
      )}

      {!loading && !error && (
        <EventTimeline
          events={events}
          onEventClick={setSelectedEvent}
          onToggleComplete={handleToggleComplete}
          onDelete={handleDeleteRequest}
          onEdit={handleEdit}
        />
      )}

      <EventDetailModal
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        onEdit={(ev) => { setSelectedEvent(null); handleEdit(ev); }}
        onDelete={handleDeleteRequest}
      />

      <ConfirmationDialog
        open={!!eventToDelete}
        onClose={() => setEventToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Видалити подію"
        message={`Видалити "${eventToDelete?.title}"? Цю дію не можна скасувати.`}
        confirmText="Видалити"
        cancelText="Скасувати"
      />

      <BottomBar onQuickAdd={handleQuickAdd} />

      <QuickFoodAddModalV2
        open={foodModalOpen}
        onClose={() => { setFoodModalOpen(false); setEditingAiFood(null); }}
        selectedDate={date}
        onRefreshDashboard={refetch}
        eventToEdit={editingAiFood?.raw ?? null}
      />
      <QuickWorkoutAddModalV2
        open={workoutModalOpen}
        onClose={() => { setWorkoutModalOpen(false); setEditingAiWorkout(null); }}
        selectedDate={date}
        onRefreshDashboard={refetch}
        eventToEdit={editingAiWorkout?.raw ?? null}
      />
      <QuickMedicationAddModalV2
        open={medicationModalOpen}
        onClose={() => { setMedicationModalOpen(false); setEditingAiMedication(null); }}
        selectedDate={date}
        onRefreshDashboard={refetch}
        eventToEdit={editingAiMedication?.raw ?? null}
      />
      <AddFoodEventModal
        open={manualFoodModalOpen}
        onClose={() => { setManualFoodModalOpen(false); setEditingManualFood(null); }}
        onSave={handleSaveManualFood}
        eventToEdit={editingManualFood}
      />
      <AddWorkoutEventModal
        open={manualWorkoutModalOpen}
        onClose={() => { setManualWorkoutModalOpen(false); setEditingManualWorkout(null); }}
        onSave={handleSaveManualWorkout}
        eventToEdit={editingManualWorkout}
      />
      <AddMedicationEventModal
        open={manualMedicationModalOpen}
        onClose={() => { setManualMedicationModalOpen(false); setEditingManualMedication(null); }}
        onSave={handleSaveManualMedication}
        eventToEdit={editingManualMedication}
      />
      <AddFeelingEventModalV2
        open={feelingModalOpen}
        onClose={() => { setFeelingModalOpen(false); setEditingFeeling(null); }}
        onSave={handleSaveFeeling}
        eventToEdit={editingFeeling}
      />
      <AddTaskEventModalV2
        open={taskModalOpen}
        onClose={() => { setTaskModalOpen(false); setEditingTask(null); }}
        onSave={handleSaveTask}
        initialScheduledAt={new Date(`${date}T${new Date().toTimeString().slice(0, 5)}`)}
        eventToEdit={editingTask}
      />
      <QuickWaterAddModal
        open={waterModalOpen}
        onClose={() => { setWaterModalOpen(false); setEditingWater(null); }}
        selectedDate={date}
        onRefreshDashboard={refetch}
        eventToEdit={editingWater}
      />

      <AiAnalysisModal
        open={aiAnalysisOpen}
        onClose={() => setAiAnalysisOpen(false)}
        from={date}
        to={date}
      />

    </Box>
  );
};
